import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import type { OnlyFansMessage } from '@api'
import { useUnit } from 'effector-react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import Heart from '@/assets/images/heart.gif'
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { $activeChatDetails, $fanId } from '@/models/chattingUI/chats/chats.ts'
import {
  markMessagesAsRead,
  markMessagesAsUnread,
  removeReceivedMessage,
  selectorIsMessageUnread,
  selectorIsNewMessage,
} from '@/models/chattingUI/unreadChatMessages'
import { useDocumentVisibility } from '@/modules/chatting/hooks/useDocumentVisibility.ts'
import MoreIcon from '@/modules/chatting/icons/more.svg'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { classNames } from '@/utils/helpers.ts'

import type { MessageProps } from './ChatMessage.tsx'
import { ChatMessageMedias } from './ChatMessageMedias'
import { ChatMessageReleaseForms } from './ChatMessageReleaseForms.tsx'
import { ChatMessageStory } from './ChatMessageStory'
import { ChatMessageText } from './ChatMessageText'
import { MessageReplyPreview } from './MessageReplyPreview.tsx'
import { PriceTag } from './PriceTag'

interface Props extends MessageProps {
  messageId: string
  belongsToPrevMessage: boolean
  onViewMessageClick: (m?: OnlyFansMessage) => void
  dropdownContent: ReactNode
  messageTag?: ReactNode
  likeMessage: () => void
  isLikeMessageLoading: boolean
}

export const ChatMessageFromPartner = ({
  messageId,
  belongsToPrevMessage,
  onViewMessageClick,
  message,
  hasMessage,
  dropdownContent,
  messageTag,
  searchedKeyword,
  likeMessage,
  isLikeMessageLoading,
}: Props) => {
  const { isDocumentVisible, addToExecutionQueue } = useDocumentVisibility()
  const ref = useRef<HTMLDivElement>(null)
  const activeChat = useUnit($activeChatDetails)
  const fanId = useUnit($fanId)

  const { media, releaseForms, replyToMessage, story } = message
  const isUnread = useUnit(selectorIsMessageUnread(fanId, message.id))
  const isNewMessage = useUnit(selectorIsNewMessage(fanId, message.id))

  const [doubleClickPosition, setDoubleClickPosition] = useState({
    x: 0,
    y: 0,
  })

  const handleLikeMessage = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left //x position within the element.
      const y = event.clientY - rect.top //y position within the element.

      setDoubleClickPosition({ x, y })
      likeMessage()
    },
    [likeMessage],
  )

  useEffect(() => {
    // Only observe the message if it's unread
    if (!ref.current || !isNewMessage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!fanId) return

        if (entry.isIntersecting) {
          if (isDocumentVisible.current) {
            markMessagesAsRead({
              chatId: fanId,
              messageIds: [message.id],
            })
          } else {
            // ...otherwise, defer marking as read until the user returns to the tab.
            // This ensures unread counts are accurate if the user is away.
            addToExecutionQueue(() =>
              markMessagesAsRead({
                chatId: fanId,
                messageIds: [message.id],
              }),
            )
          }

          removeReceivedMessage({
            chatId: fanId,
            messageId: message.id,
          })

          observer.disconnect()
        } else {
          markMessagesAsUnread({
            chatId: fanId,
            messageIds: [message.id],
          })
        }
      },
      { threshold: 0.5 },
    )

    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [
    addToExecutionQueue,
    fanId,
    isDocumentVisible,
    isNewMessage,
    isUnread,
    message.id,
    message.isReadByCreator,
    message.text,
    messageId,
  ])

  return (
    <div
      id={messageId}
      ref={ref}
      className={classNames({
        relative: true,
        'flex w-full flex-col': true,
        'mt-4': !belongsToPrevMessage,
        'mt-1': belongsToPrevMessage,
      })}
    >
      <div className="flex w-full">
        {!belongsToPrevMessage && (
          <CreatorAvatar
            className="mr-4 shrink-0"
            size={40}
            name={activeChat?.withUser?.name ?? ''}
            avatarSrc={
              activeChat?.withUser?.avatarThumbs?.c50 ??
              activeChat?.withUser?.avatarThumbs?.c144 ??
              activeChat?.withUser?.avatar
            }
          />
        )}
        <div
          className={classNames('w-[calc(100%-60px)]', {
            'pl-[56px]': belongsToPrevMessage,
          })}
        >
          <div className="relative flex items-end">
            <div className="max-w-[60%]">
              {story && <ChatMessageStory story={story} />}
              <div
                className={classNames(
                  'break-word bg-secondary-100 dark:bg-secondary-700 break-words rounded-bl-[8px] rounded-br-[8px] rounded-tr-[8px] px-3 py-2.5',
                  {
                    'bg-red-200 dark:bg-red-900': message.isDeleted,
                    'pb-5': !message.isFree,
                  },
                )}
                onDoubleClick={handleLikeMessage}
              >
                {isLikeMessageLoading && (
                  <img
                    src={Heart}
                    className="absolute w-[30px] -translate-x-1/2 -translate-y-full"
                    alt="heart icon"
                    style={{
                      top: doubleClickPosition.y,
                      left: doubleClickPosition.x,
                    }}
                  />
                )}
                <MessageReplyPreview
                  onViewMessageClick={onViewMessageClick}
                  message={replyToMessage}
                  canView={hasMessage(replyToMessage?.id ?? 0)}
                />
                {(!!media?.length || message.giphyId) && (
                  <ChatMessageMedias
                    message={message}
                    medias={media}
                    isMessageFromFan
                  />
                )}
                <ChatMessageText
                  message={message}
                  searchedKeyword={searchedKeyword}
                />
                {message.isDeleted && (
                  <div className="border-danger-default flex w-full gap-1 border-t pt-1">
                    <InfoIcon className="text-danger-default h-4 w-4" />
                    <span className="text-xs font-medium italic">
                      {message.deletedByUserName
                        ? `deleted by ${message.deletedByUserName}`
                        : 'deleted'}
                    </span>
                  </div>
                )}
                {!!releaseForms?.length && (
                  <ChatMessageReleaseForms message={message} />
                )}
              </div>
            </div>
            {!message.isDeleted && (
              <DropdownMenu>
                <DropdownMenuTrigger className="ml-2 h-10">
                  <MoreIcon className="text-secondary-300 hover:text-primary-500" />
                </DropdownMenuTrigger>
                {dropdownContent}
              </DropdownMenu>
            )}
            {!message.isFree && (
              <div className="absolute -bottom-[17px] left-0">
                <PriceTag message={message} />
              </div>
            )}
          </div>
          {messageTag}
        </div>
      </div>
    </div>
  )
}
